import React from 'react';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './AttachmentCard.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

interface AttachmentCardBase {
  theme?: ChatTheme;
  className?: string;
}

/**
 * The dot is **not** an opt-in affordance — §1.12 lists it under "Not a prop" and all six
 * artboards draw it, *With attachments* included, where the message is already sent and nothing
 * can be removed. So the dot is always painted. What this pair decides is whether it is a
 * *control*:
 * with a handler it is a real `<button>` and therefore needs a name; without one it is the
 * decoration Paper drew, kept out of the tab order and out of the accessibility tree rather than
 * offered as a focusable no-op.
 */
type AttachmentCardRemove =
  | {
      /** Fires from the 20px dot pinned to the top-right corner. */
      onRemove: () => void;
      /**
       * Accessible name for that dot. Icon-only control, so BUILD-CONTRACT §6 makes it required —
       * the same rule, and the same reason, as `IconSlot`'s `label`. There is no synthesised
       * default: a caller who knows the file knows a better name than `Remove {name}`.
       */
      removeLabel: string;
    }
  | { onRemove?: never; removeLabel?: never };

export type AttachmentCardProps = AttachmentCardBase &
  AttachmentCardRemove &
  (
    | {
        variant: 'titled';
        /** Free text, not an enum — Paper ships `Slide`, `Doc`, `Data` and `Deck` across six boards. */
        kind: string;
        /** Truncates to one line in the 76px content column. That truncation is the design. */
        name: string;
      }
    | {
        variant: 'image';
        /** The media stand-in: a solid colour until real thumbnails exist. Any CSS colour. */
        fill: string;
      }
  );

/**
 * A single attachment chip: a fixed 96x96 square carrying a kind label, a truncating name and a
 * remove dot.
 *
 * The two variants are the same box. `image` swaps the light-6 ground for the media and empties
 * both text slots — but **keeps both 18px rows as spacers**, so an image chip and a titled chip
 * are pixel-identical in every dimension and can sit side by side in one tray (*Images* next to
 * *Attachments*). Emptying the rows instead of removing them is the whole reason there is one
 * component
 * here and not two.
 */
export function AttachmentCard(props: AttachmentCardProps) {
  const { onRemove, removeLabel, theme: themeProp, className } = props;
  const theme = useV15Theme(themeProp);

  return (
    <div
      className={cn(
        chatThemeClass(theme),
        styles.card,
        props.variant === 'image' && styles.image,
        className,
      )}
      // The only runtime value on the card, so the only thing that may be an inline style.
      style={
        props.variant === 'image'
          ? ({ '--cv15-attachment-fill': props.fill } as React.CSSProperties)
          : undefined
      }
    >
      <div className={styles.kindRow}>
        {props.variant === 'titled' ? <span className={styles.kind}>{props.kind}</span> : null}
      </div>
      <div className={styles.spacer} />
      <div className={styles.nameRow}>
        {props.variant === 'titled' ? (
          <div className={styles.nameClip}>
            <span className={styles.name} title={props.name}>
              {props.name}
            </span>
          </div>
        ) : null}
      </div>
      {/* The dot is always drawn — it is what makes this an attachment chip rather than a tile,
          and every artboard has one. Only its element changes: a real `<button>` where Paper drew
          a `<div>` when there is something to remove, an inert `aria-hidden` span when there is
          not, so a sent message's card looks identical without putting a focusable
          control that does nothing into the tab order. */}
      {/* 12px — the one rung of the glyph ladder that is not on a text line. Both branches draw
          the identical mark at `tone="inherit"`, which is what keeps them identical: the tone lives
          on `.remove` (subtle) and its lift to full text lives on `.removeControl:hover`, so the
          picture and the control cannot drift apart by one of them being told a colour. */}
      {onRemove ? (
        <button
          type="button"
          className={cn(styles.remove, styles.removeControl)}
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <ActivityGlyph chrome="close" size={12} tone="inherit" theme={theme} />
        </button>
      ) : (
        <span className={styles.remove} aria-hidden="true">
          <ActivityGlyph chrome="close" size={12} tone="inherit" theme={theme} />
        </span>
      )}
    </div>
  );
}
